import React from "react";

interface Breadcrumb {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface PageHeaderProps {
  variant?: "breadcrumb" | "breadcrumb-with-icon";
  breadcrumbs: Breadcrumb[];
  onToggleSidebar?: () => void;
  searchAction?: () => void;
  secondaryAction?: { label: string; onClick: () => void };
  primaryAction?: { label: string; onClick: () => void; disabled?: boolean };
  actions?: React.ReactNode;
}

const ExpandMenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="2.5" y="2.5" width="15" height="15" rx="2.5" stroke="var(--color-content-secondary)" strokeWidth="1.5" />
    <path d="M7.5 2.5V17.5" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667Z" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 14l-2.9-2.9" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const crumbLabel: React.CSSProperties = {
  fontFamily: "var(--font-family-base)",
  fontSize: 12,
  fontWeight: 700,
  lineHeight: "16px",
  letterSpacing: "-0.24px",
  whiteSpace: "nowrap",
  padding: "4px 0",
};

const crumbTitle: React.CSSProperties = {
  fontFamily: "var(--font-family-base)",
  fontSize: 18,
  fontWeight: 700,
  lineHeight: "28px",
  letterSpacing: "-0.36px",
  whiteSpace: "nowrap",
};

export function PageHeader({
  variant = "breadcrumb-with-icon",
  breadcrumbs,
  onToggleSidebar,
  searchAction,
  secondaryAction,
  primaryAction,
  actions,
}: PageHeaderProps) {
  return (
    <header style={{
      height: 72,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 var(--spacing-2xl)",
      borderBottom: "1px solid var(--color-border-primary)",
      background: "var(--color-bg-primary)",
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-s)", flex: 1, minWidth: 0 }}>
        {variant === "breadcrumb-with-icon" && (
          <button
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <ExpandMenuIcon />
          </button>
        )}
        <nav aria-label="Breadcrumb" style={{ display: "flex", alignItems: "center", gap: "var(--spacing-s)" }}>
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            const isSingleCrumb = breadcrumbs.length === 1;
            const textStyle = isSingleCrumb ? crumbTitle : crumbLabel;
            const color = isLast ? "var(--color-content-primary)" : "var(--color-content-secondary)";
            return (
              <React.Fragment key={i}>
                {crumb.onClick ? (
                  <button
                    onClick={crumb.onClick}
                    style={{ ...textStyle, color, background: "none", border: "none", cursor: "pointer" }}
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span style={{ ...textStyle, color }}>{crumb.label}</span>
                )}
                {!isLast && (
                  <span style={{ ...crumbLabel, color: "var(--color-content-tertiary)" }}>/</span>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-s)", flexShrink: 0 }}>
        {actions ?? (
          <>
            {searchAction && (
              <button
                onClick={searchAction}
                aria-label="Search"
                style={{
                  width: 40,
                  height: 40,
                  background: "var(--color-bg-accent)",
                  border: "none",
                  borderRadius: "var(--radius-s)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <SearchIcon />
              </button>
            )}
            {secondaryAction && (
              <button
                onClick={secondaryAction.onClick}
                style={{
                  height: 40,
                  padding: "0 var(--spacing-s)",
                  background: "var(--color-bg-primary)",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: "var(--radius-s)",
                  fontFamily: "var(--font-family-base)",
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: "16px",
                  letterSpacing: "-0.24px",
                  color: "var(--color-content-primary)",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {secondaryAction.label}
              </button>
            )}
            {primaryAction && (
              <button
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
                style={{
                  height: 40,
                  padding: "0 var(--spacing-s)",
                  background: primaryAction.disabled ? "var(--color-border-secondary)" : "var(--color-bg-brand-dark)",
                  border: "none",
                  borderRadius: "var(--radius-s)",
                  fontFamily: "var(--font-family-base)",
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: "16px",
                  letterSpacing: "-0.24px",
                  color: "var(--color-content-inverse)",
                  cursor: primaryAction.disabled ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {primaryAction.label}
              </button>
            )}
          </>
        )}
      </div>
    </header>
  );
}
